import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MotionConfig } from 'framer-motion';
import { useAllBoardsWithCards } from '../hooks/useAllBoardsWithCards';
import { useMultiBoardSubscription } from '../hooks/useMultiBoardSubscription';
import { buildPhaseUnion } from '../lib/phase-union';
import { CommandPhaseLane } from '../components/CommandPhaseLane';
import { QuickCreateCardModal } from '../components/QuickCreateCardModal';
import { getBoardColorHex } from '../lib/utils';
import type { Card } from '../lib/api';

export function CommandCenter() {
  const navigate = useNavigate();
  const [showCreate, setShowCreate] = useState(false);
  const [quickCreateBoardId, setQuickCreateBoardId] = useState<string | undefined>();

  const {
    boards,
    boardsById,
    allCards,
    workflowsByBoardId,
    failedBoardIds,
    isLoading,
    isError,
  } = useAllBoardsWithCards();

  const boardIds = useMemo(() => boards.map((b) => b.id), [boards]);
  useMultiBoardSubscription(boardIds);

  const phaseUnion = useMemo(
    () => buildPhaseUnion({ boards, workflowsByBoardId }),
    [boards, workflowsByBoardId],
  );

  const cardsByPhaseKey = useMemo(() => {
    const map = new Map<string, Card[]>();
    for (const p of phaseUnion) map.set(p.key, []);
    for (const card of allCards) {
      const bucket = map.get(card.status);
      if (bucket) bucket.push(card);
    }
    return map;
  }, [phaseUnion, allCards]);

  const handleCardClick = (card: Card) => {
    navigate(`/boards/${card.boardId}?card=${card.id}`);
  };

  if (isLoading && boards.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-sm text-red-400">Failed to load boards.</p>
      </div>
    );
  }

  if (boards.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 p-6 text-center">
        <p className="text-sm text-gray-400">
          Command Center shows every card across every board in one place.
        </p>
        <p className="text-xs text-gray-500">Create a board from the Dashboard to get started.</p>
        <button
          onClick={() => navigate('/')}
          className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-500"
        >
          Go to Dashboard
        </button>
      </div>
    );
  }

  return (
    <MotionConfig reducedMotion="user">
      <div className="flex h-full flex-col">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-800 px-4 py-3 md:px-6 md:py-4">
          <div className="flex min-w-0 items-center gap-3">
            <h1 className="truncate text-lg font-semibold">Command Center</h1>
            <span className="hidden text-xs text-gray-500 md:inline">
              {boards.length} board{boards.length !== 1 ? 's' : ''} · {allCards.length} card
              {allCards.length !== 1 ? 's' : ''}
            </span>
          </div>
          <button
            onClick={() => {
              setQuickCreateBoardId(undefined);
              setShowCreate(true);
            }}
            className="flex items-center gap-2 rounded-lg bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-500"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            New Card
          </button>
        </div>

        {/* Board legend + per-board retry pills */}
        <div className="flex flex-wrap items-center gap-2 border-b border-gray-800 px-4 py-2 md:px-6">
          {boards.map((b) => {
            const failed = failedBoardIds.includes(b.id);
            const hex = getBoardColorHex(b.settings?.color as string | undefined);
            return (
              <button
                key={b.id}
                onClick={() => {
                  setQuickCreateBoardId(b.id);
                  setShowCreate(true);
                }}
                className={`flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] transition-colors ${
                  failed
                    ? 'border-red-500/50 text-red-400 hover:bg-red-500/10'
                    : 'border-gray-800 text-gray-400 hover:bg-gray-800 hover:text-gray-200'
                }`}
                title={failed ? `${b.name} failed to load — click to retry` : `New card in ${b.name}`}
              >
                <span className="h-2 w-2 rounded-full" style={{ background: hex }} />
                {b.name}
                {failed && <span className="ml-1 text-[10px] font-semibold">retry</span>}
              </button>
            );
          })}
        </div>

        {/* Lanes */}
        <div className="flex-1 overflow-y-auto">
          <div className="flex flex-col gap-2 p-4 md:p-6">
            {phaseUnion.map((phase) => (
              <CommandPhaseLane
                key={phase.key}
                phase={phase}
                cards={cardsByPhaseKey.get(phase.key) ?? []}
                boardsById={boardsById}
                onCardClick={handleCardClick}
              />
            ))}
            {phaseUnion.length === 0 && (
              <div className="flex h-32 items-center justify-center text-xs text-gray-500">
                No phases configured yet.
              </div>
            )}
          </div>
        </div>

        {showCreate && (
          <QuickCreateCardModal
            boards={boards}
            workflowsByBoardId={workflowsByBoardId}
            initialBoardId={quickCreateBoardId}
            onClose={() => setShowCreate(false)}
          />
        )}
      </div>
    </MotionConfig>
  );
}
